import { createSelector } from 'reselect';

import {
    AuthSignInSystemType,
    IAuthSignInSystem,
    IExternalSignInSystemV1ConfigDto,
    IExternalSignInSystemV2ConfigDto,
    IExternalSignInSystemV3ConfigDto,
    mapExternalSignInSystemV1,
    mapExternalSignInSystemV2,
    mapExternalSignInSystemV3,
} from '@pressreader/authentication-types';
import { selectGetConfigValue } from '@pressreader/config';

export const selectExternalSignInSystems = createSelector(selectGetConfigValue, getConfigValue => {
    const externalSystems = getConfigValue<IExternalSignInSystemV1ConfigDto[]>('ui.SignIn.ExternalSystems') || [];
    return externalSystems.map(mapExternalSignInSystemV1);
});

export const selectExternalSignInSystemsV2 = createSelector(selectGetConfigValue, getConfigValue => {
    const featuredSystem = getConfigValue<AuthSignInSystemType>('features.app.auth.featuredSystem');
    const externalSystemsMap = getConfigValue<Record<string, IExternalSignInSystemV2ConfigDto>>('ui.SignIn.ExternalSystemsV2') || {};

    const systems: IAuthSignInSystem[] = [];
    for (const name in externalSystemsMap) {
        const dto = externalSystemsMap[name];

        if (dto.enabled === 'true') {
            const featured = name === featuredSystem;
            systems.push(mapExternalSignInSystemV2(name, dto, featured));
        }
    }

    return systems;
});

export const selectFeaturedSignInSystem = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<AuthSignInSystemType>('features.app.auth.featuredsystem'),
);

export const selectExternalSignInSystemsV3Map = createSelector(
    selectGetConfigValue,
    getConfigValue => getConfigValue<Record<string, IExternalSignInSystemV3ConfigDto>>('ui.SignIn.ExternalSystemsV3') || {},
);

export const selectExternalSignInSystemsV3 = createSelector(
    selectFeaturedSignInSystem,
    selectExternalSignInSystemsV3Map,
    (featuredSystem, externalSystemsMap) => {
        const systems: IAuthSignInSystem[] = [];
        for (const name in externalSystemsMap) {
            const dto = externalSystemsMap[name];

            const featured = name === featuredSystem;
            systems.push(mapExternalSignInSystemV3(name, dto, featured));
        }

        return systems;
    },
);

export const selectIsEmailOnlySignIn = createSelector(
    selectExternalSignInSystemsV3,
    systems => systems.length === 0 || (systems.length === 1 && systems[0].type === AuthSignInSystemType.Email),
);

// True when the publisher configures an external/outside authentication flow (e.g. Auth0). In that
// case the sign-in/sign-up screens must redirect to the client's auth instead of showing the native UI.
export const selectIsExternalAuthRedirectEnabled = createSelector(selectGetConfigValue, getConfigValue => {
    const oAuthCfg = getConfigValue<{ Name?: string } | string>('Authentication.OAuthProvider');
    const oAuthProvider = typeof oAuthCfg === 'object' ? oAuthCfg?.Name : oAuthCfg;

    return (
        (getConfigValue<boolean>('Authentication.EnablePublisherAuthenticationWebService', false) &&
            !!getConfigValue<string>('Authentication.ExternalAuthenticationPage', '')) ||
        getConfigValue<boolean>('Authentication.EnableExternalAuthenticationScreenSet', false) ||
        !!oAuthProvider
    );
});

export const selectAllowPromoNotif = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<boolean>('features.signup.allowpromotionalnotifications', false),
);

export const selectAllowServiceNotif = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<boolean>('features.signup.allowservicenotifications', false),
);
